/**
 * CoSignerEditor — co-signer (guarantor) list plus add form.
 * Lifted verbatim from Step3Tenants so the lease wizard and the amendment flow share one form.
 */

import { useState } from 'react';
import type { CoSigner } from '../../types/lease-generation';
import { RequiredMark } from '../../pages/lease-wizard/RequiredMark';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { validateCoSigner } from './pet-validation';

export interface CoSignerEditorProps {
  coSigners: CoSigner[];
  addCoSigner: (coSigner: CoSigner) => void;
  removeCoSigner: (index: number) => void;
  /** The amendment API's CoSignerSchema requires a phone number; the wizard leaves it optional. */
  requirePhone?: boolean;
}

export function CoSignerEditor({ coSigners, addCoSigner, removeCoSigner, requirePhone }: CoSignerEditorProps) {
  const [coSignerForm, setCoSignerForm] = useState({ name: '', email: '', phone: '', mailingAddress: '' });
  const [coSignerError, setCoSignerError] = useState<string | null>(null);

  const handleAddCoSigner = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const error = validateCoSigner(coSignerForm, { requirePhone });
    if (error) {
      setCoSignerError(error);
      return;
    }
    addCoSigner({
      name: coSignerForm.name.trim(),
      email: coSignerForm.email.trim(),
      phone: coSignerForm.phone.trim(),
      mailingAddress: coSignerForm.mailingAddress.trim(),
    });
    setCoSignerForm({ name: '', email: '', phone: '', mailingAddress: '' });
    setCoSignerError(null);
  };

  const labelClass = 'block mb-1 text-sm font-medium text-foreground';

  return (
      <div className="border-t border-border pt-6 mt-2" data-testid="co-signer-section">
        <div className="mb-4">
          <h3 className="text-base font-semibold text-foreground mb-1">Co-Signers (Optional)</h3>
          <p className="text-sm text-muted-foreground">
            Co-signers guarantee the tenant's lease obligations. They will receive an email signing
            invitation and do not need a platform account.
          </p>
        </div>

        {coSigners.length > 0 && (
          <div className="flex flex-col gap-2 mb-4">
            {coSigners.map((coSigner, index) => (
              <div
                key={index}
                data-testid={`co-signer-card-${index}`}
                className="flex items-start justify-between gap-3 p-4 border border-border rounded-lg bg-card"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground">{coSigner.name}</div>
                  <div className="text-xs text-muted-foreground">{coSigner.email}</div>
                  {coSigner.phone && (
                    <div className="text-xs text-muted-foreground">{coSigner.phone}</div>
                  )}
                  <div className="text-xs text-muted-foreground mt-0.5">{coSigner.mailingAddress}</div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  data-testid={`remove-co-signer-${index}`}
                  onClick={() => removeCoSigner(index)}
                  className="min-h-[44px] text-destructive border-destructive/30 hover:bg-destructive/5 shrink-0"
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="border border-border rounded-lg p-4 bg-accent/50" data-testid="add-co-signer-form">
          <h4 className="text-sm font-semibold text-foreground mb-3">Add Co-Signer</h4>
          {coSignerError && (
            <div id="co-signer-error" className="p-3 mb-3 bg-destructive/5 border border-destructive/20 rounded text-sm text-destructive" role="alert">
              {coSignerError}
            </div>
          )}
          <form onSubmit={handleAddCoSigner} className="flex flex-col gap-3">
            <div>
              <label htmlFor="co-signer-name" className={labelClass}>Full Name <RequiredMark /></label>
              <Input
                id="co-signer-name"
                type="text"
                data-testid="co-signer-name-input"
                value={coSignerForm.name}
                onChange={e => setCoSignerForm(f => ({ ...f, name: e.target.value }))}
                className="min-h-[44px]"
                placeholder="e.g. Jane Smith"
                required
                aria-required="true"
                aria-describedby={coSignerError ? 'co-signer-error' : undefined}
                aria-invalid={coSignerError ? true : undefined}
              />
            </div>
            <div>
              <label htmlFor="co-signer-email" className={labelClass}>Email <RequiredMark /></label>
              <Input
                id="co-signer-email"
                type="email"
                data-testid="co-signer-email-input"
                value={coSignerForm.email}
                onChange={e => setCoSignerForm(f => ({ ...f, email: e.target.value }))}
                className="min-h-[44px]"
                placeholder="e.g. jane@example.com"
                required
                aria-required="true"
                aria-describedby={coSignerError ? 'co-signer-error' : undefined}
                aria-invalid={coSignerError ? true : undefined}
              />
            </div>
            <div>
              <label htmlFor="co-signer-phone" className={labelClass}>Phone{requirePhone && <> <RequiredMark /></>}</label>
              <Input
                id="co-signer-phone"
                type="tel"
                data-testid="co-signer-phone-input"
                value={coSignerForm.phone}
                onChange={e => setCoSignerForm(f => ({ ...f, phone: e.target.value }))}
                className="min-h-[44px]"
                placeholder="e.g. (555) 123-4567"
                required={requirePhone}
                aria-required={requirePhone ? 'true' : undefined}
                aria-describedby={coSignerError ? 'co-signer-error' : undefined}
                aria-invalid={coSignerError ? true : undefined}
              />
            </div>
            <div>
              <label htmlFor="co-signer-mailing-address" className={labelClass}>Mailing Address <RequiredMark /></label>
              <Input
                id="co-signer-mailing-address"
                type="text"
                data-testid="co-signer-address-input"
                value={coSignerForm.mailingAddress}
                onChange={e => setCoSignerForm(f => ({ ...f, mailingAddress: e.target.value }))}
                className="min-h-[44px]"
                placeholder="e.g. 123 Main St, Springfield, IL 62701"
                required
                aria-required="true"
                aria-describedby={coSignerError ? 'co-signer-error' : undefined}
                aria-invalid={coSignerError ? true : undefined}
              />
            </div>
            <div className="flex justify-end mt-1">
              <Button
                type="submit"
                data-testid="add-co-signer-button"
                className="min-h-[44px]"
              >
                Add Co-Signer
              </Button>
            </div>
          </form>
        </div>
      </div>
  );
}
